import { Heading, Icon } from '@postscript/components';
import React from 'react';
import styled from 'styled-components';

// Each flush section card has an icon-prefixed title with a divider beneath
// it, padded to match the row hairline grid (`px 12px / py 18px` per Figma).
// The icon is optional so non-card surfaces could reuse this without one.
const HeaderRow = styled.div<{ $divider: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  padding: var(--spacing-3) var(--spacing-2);
  ${({ $divider }) =>
    $divider && `border-bottom: 1px solid var(--border-color-dim);`}
`;

// Pushes the optional action button to the far right of the header row.
const ActionButton = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--text-color-dim);

  &:hover,
  &:focus-visible {
    color: var(--link-color);
  }
`;

type SectionHeaderIcon = React.ComponentProps<typeof Icon>['component'];

interface SectionHeaderAction {
  // A member of @postscript/components IconSet, e.g. IconSet.ArrowUpRight.
  icon: SectionHeaderIcon;
  onClick: () => void;
  // Accessible label for the icon-only button, e.g. "View subscriber".
  label: string;
}

interface SectionHeaderProps {
  id?: string;
  children: React.ReactNode;
  // A member of @postscript/components IconSet, e.g. IconSet.User.
  icon?: SectionHeaderIcon;
  // Render the divider under the title (used inside section cards).
  divider?: boolean;
  // Optional right-aligned icon button, e.g. a "view subscriber" external link.
  action?: SectionHeaderAction;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  id,
  children,
  icon,
  divider = false,
  action,
}) => (
  <HeaderRow $divider={divider}>
    {icon && <Icon component={icon} size={24} color="var(--text-color)" />}
    <Heading as="h3" size="xx-small" id={id}>
      {children}
    </Heading>
    {action && (
      <ActionButton
        type="button"
        onClick={action.onClick}
        aria-label={action.label}
      >
        <Icon component={action.icon} size={16} />
      </ActionButton>
    )}
  </HeaderRow>
);
